import { useState } from 'react';
import { Plus, Pencil, Trash2, Copy, ExternalLink, Loader2 } from 'lucide-react';
import PageTitle from '../components/PageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DemoConfigDimensionFilters,
  DemoConfigBlacklistedMetrics,
} from '../components/DemoConfigDimensionFilters';
import {
  useDemoConfigsList,
  useCreateDemoConfig,
  useUpdateDemoConfig,
  useDeleteDemoConfig,
} from '../hooks/useDemoConfigs';
import type { DemoConfig } from '../services/demoConfig.service';

const ADAPTIVE_THINKING_MODELS = new Set(['claude-sonnet-4-6', 'claude-opus-4-6']);

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { value: 'claude-3-5-sonnet', label: 'Claude Sonnet 3.5' },
];

function fmtDateTime(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

function isExpired(value: string): boolean {
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.getTime() < Date.now();
}

function demoUrl(id: string): string {
  return `${window.location.origin}/demo/${id}`;
}

// datetime-local <input> wants "YYYY-MM-DDTHH:mm" in local time, no "Z".
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultExpiry(): string {
  const d = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // +30 days
  return toDatetimeLocal(d.toISOString());
}

interface DemoConfigFormValues {
  client_name: string;
  logo_url: string | null;
  brand_color: string | null;
  branch: 'PROD' | 'DRAFT';
  dimension_filters: Record<string, string[]>;
  blacklisted_metrics: string[];
  chat_settings: DemoConfig['chat_settings'];
  show_history: boolean;
  chat_render_config: DemoConfig['chat_render_config'];
  expires_at: string;
  enabled: boolean;
}

function DemoConfigFormDialog({
  open,
  onOpenChange,
  initialValues,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: DemoConfig | null;
  onSubmit: (data: DemoConfigFormValues) => void;
  submitting: boolean;
}) {
  const isEditing = !!initialValues;

  const [clientName, setClientName] = useState(initialValues?.client_name || '');
  const [logoUrl, setLogoUrl] = useState(initialValues?.logo_url || '');
  const [brandColor, setBrandColor] = useState(initialValues?.brand_color || '#0F62FE');
  const [branch, setBranch] = useState<'PROD' | 'DRAFT'>(initialValues?.branch || 'PROD');
  const [dimensionFilters, setDimensionFilters] = useState<Record<string, string[]>>(
    initialValues?.dimension_filters || {}
  );
  const [blacklistedMetrics, setBlacklistedMetrics] = useState<string[]>(
    initialValues?.blacklisted_metrics || []
  );
  const [model, setModel] = useState(initialValues?.chat_settings?.model || 'claude-sonnet-4-6');
  const [verbosity, setVerbosity] = useState(initialValues?.chat_settings?.verbosity || 'low');
  const [reasoning, setReasoning] = useState(initialValues?.chat_settings?.reasoning || '');
  const [maxOutputTokens, setMaxOutputTokens] = useState(
    initialValues?.chat_settings?.max_output_tokens != null
      ? String(initialValues.chat_settings.max_output_tokens)
      : ''
  );
  const [showHistory, setShowHistory] = useState(initialValues?.show_history || false);
  const [centerInput, setCenterInput] = useState(
    initialValues?.chat_render_config?.center_input_on_new_chat || false
  );
  const [placeholder, setPlaceholder] = useState(
    initialValues?.chat_render_config?.placeholder || 'Ask a question'
  );
  const [welcomeMessage, setWelcomeMessage] = useState(
    initialValues?.chat_render_config?.welcome_message || ''
  );
  const [expiresAt, setExpiresAt] = useState(
    initialValues ? toDatetimeLocal(initialValues.expires_at) : defaultExpiry()
  );
  const [enabled, setEnabled] = useState(initialValues?.enabled ?? true);

  const supportsReasoning = ADAPTIVE_THINKING_MODELS.has(model);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName.trim() || !expiresAt) return;
    onSubmit({
      client_name: clientName.trim(),
      logo_url: logoUrl.trim() || null,
      brand_color: brandColor.trim() || null,
      branch,
      dimension_filters: dimensionFilters,
      blacklisted_metrics: blacklistedMetrics,
      chat_settings: {
        model,
        verbosity,
        reasoning: supportsReasoning ? reasoning || null : null,
        max_output_tokens: maxOutputTokens ? Number(maxOutputTokens) : null,
      },
      show_history: showHistory,
      chat_render_config: {
        center_input_on_new_chat: centerInput,
        placeholder: placeholder.trim() || 'Ask a question',
        welcome_message: welcomeMessage.trim(),
      },
      expires_at: new Date(expiresAt).toISOString(),
      enabled,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Demo' : 'New Demo'}</DialogTitle>
            <DialogDescription>
              A branded, public landing page with a "Start Chat" button — for showing a
              prospect what an embedded chat looks like on their own site. Scoped to your
              own tenant's data.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client name</Label>
                <Input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g. ServiceAutopilot"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={branch} onValueChange={(v) => setBranch(v as 'PROD' | 'DRAFT')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PROD">PROD</SelectItem>
                    <SelectItem value="DRAFT">DRAFT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://.../logo.png"
                />
              </div>
              <div className="space-y-2">
                <Label>Top bar background color</Label>
                <div className="flex items-center gap-2">
                  <div
                    className="h-9 w-9 shrink-0 rounded-md border"
                    style={{ backgroundColor: brandColor || undefined }}
                  />
                  <Input
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    placeholder="#0F62FE"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Dimension filters</Label>
              <p className="text-xs text-muted-foreground">
                Row-level scope applied to the demo session — same as a production embed.
              </p>
              <DemoConfigDimensionFilters value={dimensionFilters} onChange={setDimensionFilters} />
            </div>

            <div className="space-y-2">
              <Label>Hidden metrics</Label>
              <DemoConfigBlacklistedMetrics value={blacklistedMetrics} onChange={setBlacklistedMetrics} />
            </div>

            <div className="space-y-4 pt-2 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Verbosity</Label>
                  <Select value={verbosity} onValueChange={setVerbosity}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {supportsReasoning && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reasoning</Label>
                    <Select value={reasoning || '__default__'} onValueChange={(v) => setReasoning(v === '__default__' ? '' : v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Default (low)</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="max">Max</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Max output tokens</Label>
                    <Input
                      type="number"
                      min={1}
                      max={16384}
                      value={maxOutputTokens}
                      onChange={(e) => setMaxOutputTokens(e.target.value)}
                      placeholder="Model default"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Switch id="show-history" checked={showHistory} onCheckedChange={setShowHistory} />
                <Label htmlFor="show-history" className="cursor-pointer">
                  Show chat history sidebar
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="center-input" checked={centerInput} onCheckedChange={setCenterInput} />
                <Label htmlFor="center-input" className="cursor-pointer">
                  Center input on a new chat
                </Label>
              </div>
              <div className="space-y-2">
                <Label>Input placeholder</Label>
                <Input value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Welcome message</Label>
                <Textarea
                  value={welcomeMessage}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                  rows={2}
                  placeholder="Shown above the input on a fresh conversation. Leave blank for no greeting."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div className="space-y-2">
                <Label>Expires</Label>
                <Input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  required
                />
              </div>
              {isEditing && (
                <div className="flex items-center gap-2 pt-6">
                  <Switch id="enabled" checked={enabled} onCheckedChange={setEnabled} />
                  <Label htmlFor="enabled" className="cursor-pointer">
                    Enabled
                  </Label>
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!clientName.trim() || !expiresAt || submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : isEditing ? (
                'Save Changes'
              ) : (
                'Create Demo'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function DemoConfigsPage() {
  const { data, isLoading } = useDemoConfigsList();
  const createMutation = useCreateDemoConfig();
  const updateMutation = useUpdateDemoConfig();
  const deleteMutation = useDeleteDemoConfig();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<DemoConfig | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DemoConfig | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const configs = data?.records || [];

  const handleCopyLink = (config: DemoConfig) => {
    navigator.clipboard.writeText(demoUrl(config.id)).then(() => {
      setCopiedId(config.id);
      setTimeout(() => setCopiedId((id) => (id === config.id ? null : id)), 1500);
    });
  };

  const handleSubmit = (values: DemoConfigFormValues) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, ...values }, { onSuccess: () => setFormOpen(false) });
    } else {
      createMutation.mutate(values, { onSuccess: () => setFormOpen(false) });
    }
  };

  return (
    <div className="p-8 h-full overflow-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <PageTitle title="Demo Configs" />
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Branded, public "Start Chat" landing pages for showing prospects how an embedded
            chat looks on their own site. Each one mints real, scoped embed sessions against
            your tenant's data.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          size="sm"
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Demo
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : configs.length === 0 ? (
        <div className="rounded-md border py-12 text-center text-muted-foreground">
          No demos yet. Create one to get a shareable link.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Link</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((config) => {
                const expired = isExpired(config.expires_at);
                return (
                  <TableRow key={config.id}>
                    <TableCell className="font-medium">{config.client_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{config.branch}</Badge>
                    </TableCell>
                    <TableCell>
                      {!config.enabled ? (
                        <Badge variant="secondary">Disabled</Badge>
                      ) : expired ? (
                        <Badge variant="secondary">Expired</Badge>
                      ) : (
                        <Badge>Live</Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {fmtDateTime(config.expires_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => handleCopyLink(config)}
                        >
                          <Copy className="h-3.5 w-3.5 mr-1" />
                          {copiedId === config.id ? 'Copied!' : 'Copy'}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                          <a href={`/demo/${config.id}`} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setEditing(config);
                          setFormOpen(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setDeleteTarget(config)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <DemoConfigFormDialog
        key={editing?.id || 'new'}
        open={formOpen}
        onOpenChange={setFormOpen}
        initialValues={editing}
        onSubmit={handleSubmit}
        submitting={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.client_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the demo config. Its{' '}
              <code className="font-mono text-xs">/demo/{deleteTarget?.id}</code> link will
              stop working immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id);
                  setDeleteTarget(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
